import { useEffect, useState, type ReactNode, type ComponentType, type SVGProps } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ammanStamp } from "../lib/dates";
import { DEMO_MODE } from "../lib/demo-data";
import { roleCan, useRole, type Action } from "../lib/roles";
import {
  HaloSwash, IconAnalytics, IconBolt, IconCampaigns, IconPalette, IconContent, IconHealth, IconOverview, IconPanel,
  IconPipeline, IconRoadmap, IconSearch, IconSegments, IconSettings, IconSubscribers, IconTeam,
} from "./icons";
import { AccountMenu } from "./AccountMenu";
import { CommandPalette, type PaletteItem } from "./CommandPalette";
import { NotificationBell } from "./NotificationBell";
import { DemoChip } from "./primitives";

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

const PAGE_LABELS: { match: (path: string) => boolean; label: string }[] = [
  { match: (p) => p === "/", label: "Overview" },
  { match: (p) => p.startsWith("/content/"), label: "Designer" },
  { match: (p) => p.startsWith("/content"), label: "Newsletters" },
  { match: (p) => p.startsWith("/audience"), label: "Audience" },
  { match: (p) => p.startsWith("/broadcasts"), label: "Broadcasts" },
  { match: (p) => p.startsWith("/automations"), label: "Automations" },
  { match: (p) => p.startsWith("/design"), label: "Design Studio" },
  { match: (p) => p.startsWith("/analytics"), label: "Analytics" },
  { match: (p) => p.startsWith("/subscribers"), label: "Subscribers" },
  { match: (p) => p.startsWith("/health"), label: "Data health" },
  { match: (p) => p.startsWith("/team"), label: "Team" },
  { match: (p) => p.startsWith("/pipeline"), label: "Pipeline" },
  { match: (p) => p.startsWith("/roadmap"), label: "Roadmap" },
  { match: (p) => p.startsWith("/settings"), label: "Settings" },
];

/** The tool's own name, per the Altibbi tool design standard §1. */
const TOOL_NAME = "Newsletter";
const TOOL_NAME_AR = "النشرة البريدية";
const TEAM = "Marketing team";

/**
 * The standard asks for 3–7 navigation items under "Workspace". There were
 * eleven across four groups, numbered "1. 2. 3."; the six that make up the
 * daily work are the Workspace, and the admin tools sit in their own group
 * below — the closest existing pattern, since the standard has no admin slot.
 */
const NAV: { group: string | null; items: { path: string; label: string; icon: Icon; end?: boolean; need?: Action }[] }[] = [
  {
    group: "Workspace",
    items: [
      { path: "/", label: "Overview", icon: IconOverview, end: true },
      { path: "/content", label: "Newsletters", icon: IconContent },
      { path: "/design", label: "Design Studio", icon: IconPalette },
      { path: "/automations", label: "Automations", icon: IconBolt, need: "campaigns.write" },
      { path: "/broadcasts", label: "Broadcasts", icon: IconCampaigns, need: "campaigns.write" },
      { path: "/audience", label: "Audience", icon: IconSegments, need: "segments.view" },
      { path: "/analytics", label: "Analytics", icon: IconAnalytics, need: "analytics.view" },
    ],
  },
  {
    group: "Admin",
    items: [
      { path: "/subscribers", label: "Subscribers", icon: IconSubscribers, need: "subscribers.view" },
      { path: "/health", label: "Data health", icon: IconHealth, need: "health.view" },
      { path: "/pipeline", label: "Pipeline", icon: IconPipeline, need: "pipeline.view" },
      { path: "/team", label: "Team", icon: IconTeam, need: "team.view" },
      // Internal planning notes; not for the whole team.
      { path: "/roadmap", label: "Roadmap", icon: IconRoadmap, need: "settings.write" },
      { path: "/settings", label: "Settings", icon: IconSettings, need: "settings.write" },
    ],
  },
];

export function Shell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { role } = useRole();
  const [navOpen, setNavOpen] = useState(false);
  // Desktop-only: the source list collapses so the content pane can take the
  // width, as in Mail and Finder. Remembered between visits.
  const [railCollapsed, setRailCollapsed] = useState(
    () => localStorage.getItem("sidebar") === "collapsed",
  );
  // The Designer needs the width for the block panel and the email, so the
  // sidebar tucks away there by itself; the toggle still opens it, for that
  // visit, without changing the choice remembered for other pages.
  const inDesigner = /^\/content\/[^/]+$/.test(location.pathname);
  const [designerRailOpen, setDesignerRailOpen] = useState(false);
  useEffect(() => { setDesignerRailOpen(false); }, [inDesigner]);
  const collapsed = inDesigner ? !designerRailOpen : railCollapsed;
  const toggleRail = () => {
    if (window.matchMedia("(max-width: 767px)").matches) {
      setNavOpen((v) => !v);
      return;
    }
    if (inDesigner) { setDesignerRailOpen((v) => !v); return; }
    setRailCollapsed((prev) => {
      localStorage.setItem("sidebar", prev ? "expanded" : "collapsed");
      return !prev;
    });
  };
  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  const paletteItems: PaletteItem[] = NAV.flatMap((section) =>
    section.items
      .filter((item) => !item.need || roleCan(role, item.need))
      .map((item) => ({ path: item.path, label: item.label, group: section.group })),
  );

  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setNavOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navOpen]);

  return (
    <div className="ui-shell" data-rail={collapsed ? "collapsed" : "expanded"}>
      <button
        type="button"
        className="ui-scrim"
        data-open={navOpen ? "true" : "false"}
        aria-label="Close navigation"
        tabIndex={navOpen ? 0 : -1}
        onClick={() => setNavOpen(false)}
      />

      <aside className="ui-rail" data-open={navOpen ? "true" : "false"}>
        <div className="ui-toolblock">
          {/* The official mark from brand.altibbi.app/logo/mark.svg, in the
              36px square the standard reserves for it. Never redrawn. */}
          <span className="ui-toolblock-mark">
            <img src="/mark.svg" alt="" aria-hidden width={24} height={24} />
          </span>
          <div style={{ minWidth: 0 }}>
            <div className="ui-toolblock-name" title={buildTitle()}>{TOOL_NAME}</div>
            <div className="ui-toolblock-team">
              <span style={{ whiteSpace: "nowrap" }}>Altibbi · {TEAM}</span>
              {DEMO_MODE && <DemoChip />}
            </div>
          </div>
        </div>

        <nav className="ui-nav">
          {NAV.map((section, i) => {
            const items = section.items.filter((item) => !item.need || roleCan(role, item.need));
            if (items.length === 0) return null;
            return (
            <div key={section.group ?? `s${i}`} style={{ display: "grid", gap: 1 }}>
              {section.group && <div className="ui-nav-label">{section.group}</div>}
              {items.map((item) => <NavItem key={item.path} {...item} />)}
            </div>
            );
          })}
        </nav>

        <a className="ui-brandcard" href="https://brand.altibbi.app" target="_blank" rel="noreferrer">
          <HaloSwash width={40} height={40} />
          <span>
            <span className="ui-brandcard-title">Brand resources</span>
            <span className="ui-brandcard-sub">Guidelines and templates</span>
          </span>
        </a>
      </aside>

      <CommandPalette items={paletteItems} />
      <main className="ui-main">
        <header className="ui-topbar">
          <button
            type="button"
            className="ui-navbtn"
            aria-label={collapsed ? "Show sidebar" : "Hide sidebar"}
            aria-expanded={!collapsed}
            title={collapsed ? "Show sidebar" : "Hide sidebar"}
            onClick={toggleRail}
          >
            <IconPanel />
          </button>
          <nav className="ui-breadcrumb" aria-label="Breadcrumb">
            <span className="ui-breadcrumb-root">{TOOL_NAME}</span>
            <span aria-hidden className="ui-breadcrumb-sep">/</span>
            <span
              className="ui-topbar-title"
              aria-current="page"
            >
              {PAGE_LABELS.find((p) => p.match(location.pathname))?.label ?? "Overview"}
            </span>
          </nav>
          <div className="ui-topbar-actions">
            <button
              type="button"
              className="ui-search"
              onClick={() => window.dispatchEvent(new Event("open-command-palette"))}
            >
              <IconSearch width={16} height={16} />
              <span className="ui-search-label">Search</span>
              <kbd className="ui-search-kbd">⌘K</kbd>
            </button>
            <NotificationBell />
            <AccountMenu />
          </div>
        </header>
        <div className="ui-main-inner">
          <div key={location.pathname} className="ui-enter">{children}</div>
          <footer className="ui-footer">
            <span>Altibbi {TOOL_NAME} · Clear, considered communication for better health.</span>
            <span lang="ar" dir="rtl">الطبي · {TOOL_NAME_AR}</span>
          </footer>
        </div>
      </main>
    </div>
  );
}

/**
 * A nav row (standard §4): 44px, 10px corners, a 20px line icon. Selected is
 * the brand tint with brand-blue text at 600 and a 3px bar on the start edge —
 * never a solid blue fill, which the standard keeps for the one primary action
 * on the screen. Styling lives in tokens.css (.ui-nav-item) so :hover and the
 * start-edge bar need no script.
 */
function NavItem({ path, label, icon: Icon, end }: { path: string; label: string; icon: Icon; end?: boolean }) {
  return (
    <NavLink to={path} end={end} className="ui-nav-item">
      <Icon width={20} height={20} strokeWidth={1.4} style={{ flexShrink: 0 }} />
      <span>{label}</span>
    </NavLink>
  );
}

/** Version and commit, on hover over the tool name rather than in the way. */
function buildTitle(): string {
  const built = new Date(__APP_BUILT_AT__);
  const sha = __APP_SHA__.replace(/-dev$/, "");
  const isDev = __APP_SHA__.endsWith("-dev");
  return `v${__APP_BUILD__} · commit ${sha}${isDev ? " (uncommitted changes)" : ""} · built ${ammanStamp(built.toISOString())}`;
}
